/**
 * Native HTML5 drag-and-drop reordering for a list rendered as sibling DOM
 * elements (table rows, list items, cards, ...). Spread `getHandleProps(index)`
 * onto the element that starts the drag and `getTargetProps(index)` onto the
 * element that reacts to being dragged over (usually the row/item itself).
 * Reordering is live — `moveItem` fires on every dragover into a new
 * position, so the list visually reorders while dragging; dropping just
 * ends the gesture.
 */
import { useCallback, useRef } from 'react';
export function useDragReorder(moveItem) {
    const draggedIndexRef = useRef(null);
    const getHandleProps = useCallback((index) => ({
        draggable: true,
        onDragStart: (event) => {
            draggedIndexRef.current = index;
            event.dataTransfer.effectAllowed = 'move';
            // Firefox requires at least one setData call to treat the gesture as a
            // valid drag; the value itself is never read back.
            event.dataTransfer.setData('text/plain', String(index));
        },
        onDragEnd: () => {
            draggedIndexRef.current = null;
        },
    }), []);
    const getTargetProps = useCallback((index) => ({
        onDragOver: (event) => {
            event.preventDefault();
            const from = draggedIndexRef.current;
            if (from !== null && from !== index) {
                moveItem === null || moveItem === void 0 ? void 0 : moveItem(from, index);
                draggedIndexRef.current = index;
            }
        },
        onDrop: (event) => {
            event.preventDefault();
            draggedIndexRef.current = null;
        },
    }), [moveItem]);
    return { getHandleProps, getTargetProps };
}
